import { getErrorsFromAIMessageContent } from "@/components/Canvas/hooks/useStoryboardErrors";
import { useSelectedElements } from "@/hooks/useSelectedElements";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { Message, MessageContext } from "@/store/zustand/supabaseStore";
import { track } from "@/utils/events/tracker";
import { UploadedFile } from "@/utils/types";
import { fixJson, useCanvasId, useUserId } from "@/utils/utils";
import { v4 as uuidv4 } from "uuid";
import { EngSpecFileDiff } from "../../EngSpec/EngSpec";
import { VALID_COMMANDS } from "../useApplyChatBlock";

export interface MessageSegment {
  type:
    | "text"
    | "codeSnippet"
    | "other-block"
    | "aiCodeChange"
    | "aiChanges"
    | "action-progress"
    | "dev-errors"
    | "prd-json"
    | "figmaInput"
    | "reasoning"
    | "search-grounding-sources"
    | "vercel-deployment-message"
    | "env-variables-input";

  // make new type
  makeNew?: any;

  // text type
  content?: string;

  // other types
  commit?: any;
  file?: any;
  plan?: any;
  codeSnippet?: any;
  diffCodeSnippet?: any;

  aiCodeChange?: any;
  aiChanges?: any;

  // Parsed json object
  parsedJson?: any;
  jsonIsBroken?: boolean;

  // True when streaming, but should be false when done streaming
  jsonIncomplete?: boolean;

  toolCall?: boolean;
}

export const parseMessageContentBlocks = (
  message: Message
): MessageSegment[] => {
  let segments = [];

  if (!message) return segments;

  if (message.role === "user" && message.message_context?.codeSnippet) {
    // @ts-expect-error TS2345
    segments.push({
      type: "codeSnippet",
      codeSnippet: message.message_context?.codeSnippet,
      diffCodeSnippet: message.message_context?.diffCodeSnippet,
    });
  }

  let contentText = message.content || "";
  let toolCallBlock: {
    toolName: string;
    args?: { [key: string]: any };
  } | null = null;
  if (typeof contentText === "string" && contentText?.startsWith("[")) {
    try {
      contentText =
        // @ts-expect-error TS2345
        JSON.parse(message.content)?.find((block) => block?.type === "text")
          ?.text || "";
      toolCallBlock =
        // @ts-expect-error TS2345
        JSON.parse(message.content)?.find(
          (block) => block?.type === "tool-call"
        ) || null;
    } catch (e) {}
  } else if (Array.isArray(message.content)) {
    contentText =
      message.content.find((block) => block?.type === "text")?.text || "";
    toolCallBlock =
      message.content.find((block) => block?.type === "tool-call") || null;
  }

  if (!contentText && !toolCallBlock) {
    contentText = "No content, please try again";
  }

  const lines = typeof contentText === "string" ? contentText.split("\n") : [];
  const textBlocks = [];
  let currentBlock = "";
  let insideExampleBlock = false;
  lines.forEach((line) => {
    if (line.startsWith("```")) {
      // Start or end of an example block, treat it like text
      currentBlock += line + "\n";
      insideExampleBlock = !insideExampleBlock;
    } else if (line.startsWith("</") && !insideExampleBlock) {
      currentBlock += line;
      // @ts-expect-error TS2345
      textBlocks.push(currentBlock);
      currentBlock = "";
    } else if (line.startsWith("<") && !insideExampleBlock) {
      // @ts-expect-error TS2345
      textBlocks.push(currentBlock);
      currentBlock = line + "\n";
    } else {
      currentBlock += line + "\n";
    }
  });

  if (currentBlock.trim()) {
    // @ts-expect-error TS2345
    textBlocks.push(currentBlock.trim());
  }

  segments = segments.concat(
    // @ts-expect-error TS2769
    textBlocks.map((content, index) => {
      // @ts-expect-error TS2339
      if (!content.startsWith("<")) {
        return {
          type: "text",
          content,
        };
      }

      // @ts-expect-error TS2339
      const contentLines = content.split("\n");
      const blockClosed =
        contentLines[contentLines.length - 1].startsWith("</");

      const blockType = contentLines[0].replace("<", "").replace(">", "");

      if (blockType === "figma_input") {
        return {
          type: "figmaInput",
          content: contentLines
            .slice(1, blockClosed ? -1 : undefined)
            .join("\n")
            .replace("<", "")
            .replace(">", ""),
        };
      }

      if (blockType === "vercel_deployment_message") {
        return {
          type: "vercel-deployment-message",
          parsedJson: JSON.parse(
            contentLines
              .slice(1, blockClosed ? -1 : undefined)
              .join("\n")
              .trim()
          ),
        };
      }

      if (blockType === "dev-errors") {
        return {
          type: blockType,
          parsedJson: getErrorsFromAIMessageContent(content),
          jsonIsBroken: false,
        };
      }

      if (
        blockType === "code-change-json" ||
        blockType === "ai-changes" ||
        blockType === "action-progress" ||
        blockType == "prd-json" ||
        blockType === "reasoning" ||
        blockType === "search-grounding-sources" ||
        blockType === "env-variables-input"
      ) {
        try {
          let leftover = contentLines
            .slice(1, blockClosed ? -1 : undefined)
            .join("\n")
            .trim();

          const fixJsonRes = fixJson(leftover);
          const parsedObj = fixJsonRes.fixedObj;
          const jsonIsBroken = fixJsonRes.broken;
          const block = {
            content,
            jsonIsBroken,
            jsonIncomplete: !fixJsonRes.complete,
          };

          if (blockType === "code-change-json") {
            block["aiCodeChange"] = parsedObj;
            block["type"] = "aiCodeChange";
          } else if (blockType === "ai-changes") {
            block["aiChanges"] = parsedObj;
            block["type"] = "aiChanges";

            // Add an id and an executedActivityId to each change
            parsedObj.changes = parsedObj?.changes?.map?.((change, idx) => {
              return {
                id: `change-from-chat-${message.id}-${idx}`,
                executedActivityId: message.message_context?.savedActivityId,
                ...change,
              };
            });
          } else {
            // includes env-variables-input
            block["type"] = blockType;
            block["parsedJson"] = parsedObj;
          }
          return block;
        } catch (e) {}
      }

      return {
        type: "other-block",
        content:
          "```" +
          contentLines.slice(1, blockClosed ? -1 : undefined).join("\n") +
          "```",
      };
    })
  );

  if (message?.role === "assistant" && toolCallBlock) {
    const toolName = (toolCallBlock as { toolName: string }).toolName;
    switch (toolName) {
      case "readFilesTool":
        const filesRead = toolCallBlock.args?.filenames || [];
        // @ts-expect-error TS2345
        segments.push({
          type: "text",
          content: `Files Read: ${filesRead.join(", ")}`,
          toolCall: true,
        });
        break;
      case "applyFileEditsTool":
        const fileEdited = toolCallBlock.args?.filename || "";
        // @ts-expect-error TS2345
        segments.push({
          type: "text",
          content: `Editing File: ${fileEdited}`,
          toolCall: true,
        });
        break;
      case "grepTool":
        const filesSearched = toolCallBlock.args?.grepSearchPatterns || [];
        // @ts-expect-error TS2345
        segments.push({
          type: "text",
          content: `Searching Files Using Patterns: ${filesSearched.join(
            ", "
          )}`,
          toolCall: true,
        });
        break;
      case "supabaseDeployEdgeFunctionTool":
        const edgeFunctionPath =
          toolCallBlock.args?.relativeEdgeFunctionFilePath || "";
        // @ts-expect-error TS2345
        segments.push({
          type: "text",
          content: `Deploying edge function to Supabase: ${edgeFunctionPath}`,
          toolCall: true,
        });
        break;
      case "supabaseRunMigrationTool":
        const migrationName =
          toolCallBlock.args?.relativeMigrationFilePath || "";
        // @ts-expect-error TS2345
        segments.push({
          type: "text",
          content: `Running migration on Supabase: ${migrationName}`,
          toolCall: true,
        });
        break;
      case "runTerminalCommandTool":
        const terminalCommand = toolCallBlock.args?.command || "";
        // @ts-expect-error TS2345
        segments.push({
          type: "text",
          content: `Running terminal command: ${terminalCommand}`,
          toolCall: true,
        });
        break;
      case "restartDevServerTool":
        // @ts-expect-error TS2345
        segments.push({
          type: "text",
          content: "Restarting dev server",
          toolCall: true,
          refreshAfterResponse: true,
        });
        break;
    }
  }

  return segments;
};

export type AssistantInstruction =
  | {
      type: "fileChange";
      filepath: string;
      command: "create" | "str_replace" | "insert" | "overwrite";
      old_str?: string;
      new_str?: string;
      insert_line?: number;
      file_text?: string;
    }
  | {
      type: "newFile";
      filepath: string;
      content: string;
    }
  | {
      type: "newStoryboard";
      id: string;
      storyboardName: string;
      width?: number;
      height?: number;
      x?: number;
      y?: number;
      filepath?: string;
      contents: string;
    }
  | {
      type: "sqlMigration";
      filename: string;
    }
  | {
      type: "edgeFunctionDeployment";
      filename: string;
    }
  | {
      type: "terminalCommand";
      command: string;
    }
  | {
      type: "convexSync";
    }
  | {
      type: "reloadCodebase";
    }
  | {
      type: "restartDevServer";
    };

export type TracedAssistantInstruction = AssistantInstruction & {
  traceId?: string;
};

export type AssistantInstructionError =
  | {
      type: "blindEdit";
      filename: string;
    }
  | {
      type: "packageJsonChange";
      filename: string;
    }
  | {
      type: "textEditorError"; // filetofixwithinfo
      filename: string;
      error: string;
    }
  | {
      type: "brokenSegmentJson";
      segment: MessageSegment;
    }
  | {
      type: "incompleteSegmentJson";
      segment: MessageSegment;
      message: Message;
      filepath?: string;
    };

export const parseAssistantMessage = (
  message: Message,
  traceId?: string
): {
  instructions: TracedAssistantInstruction[];
  errors: AssistantInstructionError[];
} => {
  const instructions: AssistantInstruction[] = [];
  const errors: AssistantInstructionError[] = [];
  let reloadCodebase = false;
  let restartDevServer = false;

  const segments = parseMessageContentBlocks(message);
  const aiChangeSegments = segments.filter((s) => s.type === "aiChanges");

  for (const changeSegment of aiChangeSegments) {
    if (changeSegment.jsonIsBroken) {
      errors.push({ type: "brokenSegmentJson", segment: changeSegment });
      continue;
    }

    const aiChanges: EngSpecFileDiff[] = (
      changeSegment.aiChanges?.changes || []
    ).filter(Boolean);

    for (const aiChange of aiChanges) {
      // Handle non-file changes first, these are valid even if the JSON is
      // incomplete since they (at least for now) only rely on a single field
      // being defined.
      const {
        runSqlMigration,
        deployEdgeFunction,
        termCommand,
        runConvexSync,
        requiresRestart,
      } = aiChange as EngSpecFileDiff;

      if (requiresRestart) {
        restartDevServer = true;
      }

      if (runSqlMigration) {
        instructions.push({ type: "sqlMigration", filename: runSqlMigration });
        continue;
      }
      if (deployEdgeFunction) {
        instructions.push({
          type: "edgeFunctionDeployment",
          filename: deployEdgeFunction,
        });
        continue;
      }
      if (termCommand) {
        instructions.push({ type: "terminalCommand", command: termCommand });
        reloadCodebase = true;
        continue;
      }
      if (runConvexSync) {
        instructions.push({ type: "convexSync" });
        reloadCodebase = true;
        continue;
      }

      // From here on out, we're dealing with file changes
      const { storyboardName, size, topLeftCorner, change } = aiChange;
      if (storyboardName) {
        if (!change?.newFullFileContent || changeSegment.jsonIncomplete) {
          // Only truly an error on our part if the JSON was supposed to be complete, otherwise its just a context limit hit
          if (!changeSegment.jsonIncomplete) {
            track("AI_CHAT_ERROR", {
              data: aiChange,
              error:
                "No change.newFullFileContent property for storyboard instruction provided",
            });
          }
          continue;
        }
        instructions.push({
          type: "newStoryboard",
          id: uuidv4().toString(),
          storyboardName,
          width: size?.width,
          height: size?.height,
          x: topLeftCorner?.x,
          y: topLeftCorner?.y,
          contents: change.newFullFileContent,
        });
        restartDevServer = true;
        continue;
      }

      const { path: filepath } = aiChange;

      // Only allow complete JSONs to affect files, to avoid writing in empty values incorectly.
      const lastChange = aiChanges.indexOf(aiChange) === aiChanges.length - 1;
      const changeIsIncomplete = changeSegment.jsonIncomplete && lastChange;
      if (changeIsIncomplete) {
        errors.push({
          type: "incompleteSegmentJson",
          segment: changeSegment,
          filepath: aiChange.path,
          message,
        });
        continue;
      }

      if (!filepath) {
        track("AI_CHAT_ERROR", {
          data: aiChange,
          error: "No filepath property for instruction provided",
        });
        continue;
      }

      if (filepath.endsWith("package.json")) {
        errors.push({ type: "packageJsonChange", filename: filepath });
        continue;
      }

      if (aiChange.change?.newFullFileContent) {
        let contents = aiChange.change.newFullFileContent;
        if (typeof contents === "object") {
          contents = JSON.stringify(contents, null, 2);
        }
        instructions.push({
          type: "newFile",
          filepath: filepath,
          content: contents,
        });
        continue;
      }

      const { command } = aiChange;
      if (command) {
        if (!VALID_COMMANDS.has(command)) {
          const lowerCommand = command.toLowerCase();
          if (
            lowerCommand?.includes("read") &&
            lowerCommand?.includes("file")
          ) {
            errors.push({
              type: "textEditorError",
              filename: filepath,
              error:
                "Cannot read files inside ai-changes, use the tool call instead",
            });
          } else {
            errors.push({
              type: "textEditorError",
              filename: filepath,
              error: `Invalid command ${command || ""}`,
            });
          }
          continue;
        } else {
          instructions.push({
            type: "fileChange",
            filepath: filepath,
            command: command,
            old_str: aiChange.old_str,
            new_str: aiChange.new_str,
            insert_line: aiChange.insert_line,
            file_text: aiChange.file_text,
          });
          continue;
        }
      }
    }
  }

  if (reloadCodebase) {
    instructions.push({ type: "reloadCodebase" });
  }
  if (restartDevServer) {
    instructions.push({ type: "restartDevServer" });
  }

  return { instructions: instructions.map((i) => ({ ...i, traceId })), errors };
};

export const useCreateMessageForChat = () => {
  const canvasId = useCanvasId();
  const codeSnippet = useAiContextStore(
    (state) => state.chatContextCodeSnippet
  );
  const diffCodeSnippet = useAiContextStore(
    (state) => state.chatContextDiffCodeSnippet
  );
  const selectedConversationId = useAiActionStore(
    (state) => state.selectedConversationId
  );
  const { selectedTreeElements, selectedStoryboards } = useSelectedElements();
  const userId = useUserId();

  const createUserMessage = ({
    userPrompt,
    attachedFiles,
    chatOnlyMode,
  }: {
    userPrompt: string;
    attachedFiles?: UploadedFile[];
    chatOnlyMode?: boolean;
  }): Message => {
    const userMessageContext: MessageContext = {
      files: attachedFiles ?? [],
      totalUserSelection: [
        ...selectedTreeElements,
        ...selectedStoryboards.map((sb) => ({
          ...sb,
          isStoryboard: true,
        })),
      ],
    };
    if (codeSnippet) {
      userMessageContext.codeSnippet = codeSnippet;
      userMessageContext.diffCodeSnippet = diffCodeSnippet;
    }

    return {
      id: uuidv4().toString(),
      conversation_id: selectedConversationId || "", // This should always be set if this function is being called
      canvas_id: canvasId,
      content: userPrompt,
      role: "user",
      message_context: userMessageContext,
      created_at: new Date().toISOString(),
      tool_call_id: null,
      tool_calls: null,
      chat_only_mode: !!chatOnlyMode,
      auth0_user_id_sent: userId || null,
    };
  };

  const createAssistantMessage = (message: Partial<Message> = {}): Message => {
    return {
      id: message.id || uuidv4().toString(),
      conversation_id: selectedConversationId || "",
      canvas_id: canvasId,
      content: message.content || "",
      role: message.role || "assistant",
      message_context: message.message_context,
      tool_calls: message.tool_calls || null,
      tool_call_id: message.tool_call_id || null,
      created_at: message.created_at || new Date().toISOString(),
      auth0_user_id_sent: message.role === "user" ? userId || null : null,
      chat_only_mode: message.chat_only_mode || false,
    };
  };

  return { createUserMessage, createAssistantMessage };
};
